import { BASE_URL, HTTP_STATUS } from "@api/constant";
import VideoPlayer from "@components/Layouts/Player/VideoPlayer";
import { VideoSectionName } from "@config/SectionConstants";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import WrapperBlock from "@container/WrapperBlock";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import ThumbnailLinkCard from "@elements/Card/Thumbnail/ThumbnailLinkCard";
import fetchTMApi from "@helper/fetchTMApi";
import { BC_PLAYER_ID } from "@helper/getEnvVariables";
import type { GADataProps } from "@helper/getGtmData";
import { getGTMData } from "@helper/getGtmData";
import { LatestVideoResponse } from "@helper/transformer/useBrightcoveResponse";
import { isArrayWithElements } from "@helper/utils";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSwrApi from "@hooks/useSwrApi";
import { Route } from "@route-types/Video/+types/VideoDetailPage";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, type MetaTagsProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { VideoPayloadResponseType } from "@typings/PagePayloadType";
import { useEffect, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { fetchVideoData } from "./VideoDetail.server";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    VideoPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags({
      ...metaData,
      urlOrigin: BASE_URL,
    });
  }
}

export default function VideoDetailPage(): React.ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    VideoPayloadResponseType,
    string
  > = useLoaderData();

  const { data } = useSwrApi<LatestVideoResponse[]>("latest-videos");
  const [offset, setOffset] = useState(10);
  const [videos, setVideos] = useState<LatestVideoResponse[]>();

  useEffect(() => {
    const fetchData = () => {
      if (!data || data.length === 0) return;
      setVideos(data);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const adsSettings: AdsSettings = useGlobalAdSetting();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const playing = dataLoaderResponse.payload.currentVideo;
  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

  const handleLoadMoreVideo = async () => {
    const moreVideos = await fetchTMApi<LatestVideoResponse[]>(
      `latest-videos?offset=${offset}`,
      "GET",
    );
    setVideos((prevVideos) => [...(prevVideos ?? []), ...moreVideos]);
    setOffset((prevSize) => prevSize + 10);

    // This avoids GTM push.
    return 0;
  };

  return (
    <MainContainer
      sectionName={VideoSectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adsSettings}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between pb-5">
        <MainBlock
          sectionName={VideoSectionName}
          pageName={playing.name}
          showTitle={true}
        >
          <div className="flex flex-col lg:flex-row">
            <div className="flex pt-2 flex-col w-full">
              {playing.id && (
                <VideoPlayer
                  playing={playing}
                  headerType="h1"
                  showTitle={false}
                  playerId={BC_PLAYER_ID}
                />
              )}
            </div>
          </div>
        </MainBlock>
        <div className="lg:w-4/12 pl-xs pr-xxs min-h-[800px] overflow-hidden">
          <div
            id="scroll-more-videos"
            data-testid="side-block"
            className="w-full h-[800px] overflow-scroll overflow-x-hidden"
          >
            <div className="w-full pt-sm">
              <span className="font-primary font-bold lg:text-2md leading-7 text-grey-400">
                மேலும் காணொளிகள்
              </span>
              <div className="w-full">
                {isArrayWithElements(videos) &&
                  videos.map((video, index) => (
                    <>
                      <ThumbnailLinkCard<LatestVideoResponse>
                        key={index}
                        variant="video"
                        {...video}
                      />
                      {index == 1 && (
                        <AdsImu2
                          sectionName={VideoSectionName}
                          rootClassName="py-md"
                          pageAdTargetType={pageAdTargetValue}
                        />
                      )}
                    </>
                  ))}
                <LoadMore onLoadMore={handleLoadMoreVideo} />
              </div>
            </div>
          </div>
        </div>
      </WrapperBlock>
    </MainContainer>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  const brightcoveId = params.level1;
  const data = await fetchVideoData(brightcoveId);

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };

  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<VideoPayloadResponseType, string>
  >;
}
